import { Directive, effect, ElementRef, inject, input } from '@angular/core';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { asyncScheduler, filter, fromEvent, map, merge, share, throttleTime } from 'rxjs';

/** Share of the content height still below the view at which the next page is requested. */
const END_DISTANCE = 0.2;
const THROTTLE_MS = 150;
/** A wheel event after this long a pause starts a new gesture; a flick's momentum doesn't. */
const WHEEL_GESTURE_GAP_MS = 400;

/**
 * Emits `scrolledToEnd` when the host, a scroll container, is scrolled down near its end — once
 * per content height, so a page that is still loading is not requested again. A wheel down at the
 * very end, where scrolling sends no more events, asks too: the way to retry a failed page, or to
 * load more of content too short to scroll. No touch equivalent: a swipe there can't be told from
 * a card dragged up a board column, and a page must not land under a drag; on touch a failed page
 * is retried by scrolling back up and down.
 *
 * The container is measured only inside its event handlers, never at setup: a read during change
 * detection forces a style recalc of the whole page before other views have bound their classes,
 * and every `transition` on them then plays on navigation.
 */
@Directive({
  selector: '[uiInfiniteScroll]',
})
export class UiInfiniteScrollDirective {
  /** On while a page loads or the last one is reached; bind it, or a failed load is not retried. */
  readonly disabled = input(false, { alias: 'uiInfiniteScrollDisabled' });

  private readonly container = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private lastScrollTop = 0;
  private emittedAtHeight = 0;
  private lastWheelAt = 0;

  // Leading and trailing: the first event of a burst is checked at once, the last one when the
  // window ends. Shared: the checks move this instance's state, so they run once per event however
  // many listeners there are.
  readonly scrolledToEnd = outputFromObservable(
    merge(
      fromEvent(this.container, 'scroll', { passive: true }).pipe(
        throttleTime(THROTTLE_MS, asyncScheduler, { leading: true, trailing: true }),
        filter(() => this.reachedNewEnd(false)),
      ),
      fromEvent<WheelEvent>(this.container, 'wheel', { passive: true }).pipe(
        // A held button is a drag (a board card): no page may land under it.
        filter((event) => event.deltaY > 0 && !event.buttons && this.startsWheelGesture()),
        filter(() => this.reachedNewEnd(true)),
      ),
    ).pipe(
      map((): void => undefined),
      share(),
    ),
  );

  constructor() {
    // The host disables it while a page loads; a load that failed leaves the content height as it
    // was, so the next scroll to the end must be able to retry.
    effect(() => {
      this.disabled();
      this.emittedAtHeight = 0;
    });
  }

  /** `atEnd`: a wheel, which counts at the very end without the content moving. */
  private reachedNewEnd(atEnd: boolean): boolean {
    const { scrollTop, scrollHeight, clientHeight } = this.container;
    const scrollingDown = scrollTop > this.lastScrollTop;

    // Only a scroll moves the position the next scroll compares against; a wheel between two
    // scroll checks would make the second one see no movement.
    if (!atEnd) {
      this.lastScrollTop = scrollTop;
    }

    if (this.disabled() || scrollHeight === this.emittedAtHeight) {
      return false;
    }

    if (atEnd ? scrollHeight - scrollTop - clientHeight > 1 : !scrollingDown) {
      return false;
    }

    if ((scrollHeight - scrollTop - clientHeight) / scrollHeight > END_DISTANCE) {
      return false;
    }

    this.emittedAtHeight = scrollHeight;

    return true;
  }

  /** One retry per wheel gesture: a trackpad flick's momentum would otherwise hammer a failing page. */
  private startsWheelGesture(): boolean {
    const now = Date.now();
    const starts = now - this.lastWheelAt > WHEEL_GESTURE_GAP_MS;

    this.lastWheelAt = now;

    return starts;
  }
}
